import React, { useCallback, useEffect, useRef, useState } from 'react';
import APIService from '../services/api';
import { CibleMini, libelleCanal as libelleCanalV2, nomDe } from './SourceurV2Suivi';

/**
 * Suivi en direct du Sourceur — GET /sourcing/dashboard, toutes les 3 s.
 *
 * Le serveur envoie `searches[]` : une entrée par campagne (statut, moteur
 * v1/v2, stats par canal, frise des passes v2, compteur d'InMails,
 * meilleurs profils). Le front lit, affiche, tolère l'absence d'un champ
 * (rien n'est inventé) — seule exception, convenue avec le back : le
 * panneau « qui gagne » est la somme front des chiffres par campagne,
 * tant que le DTO ne porte pas de champ agrégé.
 *
 * États tenus : chargement initial, route absente (« arrive côté serveur »,
 * le polling s'arrête), erreur (message serveur verbatim + réessayer),
 * liste vide (invitation à lancer une campagne), et le direct — pastille
 * animée + heure de dernière mise à jour.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const mono = { fontFamily: "'IBM Plex Mono',monospace" };
const CARD = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px' };
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });

/* Libellés de canal : jamais de nom de code à l'écran. Table partagée avec la
   carte de ciblage — elle ignorait Job-Room, qui s'affichait « jobroom ». */
const libelleCanal = (c) => libelleCanalV2(c) || '—';

/* Lecture défensive : le DTO transite par le proxy tel quel, on accepte les
   variantes de casse sans jamais fabriquer une valeur. */
const lireCampagnes = (j) => {
  // Contrat confirmé par le back : le champ est `searches[]` — les autres
  // noms restent lus par tolérance.
  const arr = j?.searches ?? j?.campaigns ?? j?.data ?? j?.campagnes ?? (Array.isArray(j) ? j : null);
  return Array.isArray(arr) ? arr : [];
};
// Pas de champ agrégé dans le DTO (confirmé) : s'il apparaît un jour il sera
// lu en priorité, sinon le panneau « qui gagne » se somme côté front sur les
// chiffres du serveur — addition et moyenne pondérée, rien d'inventé.
const lireAgrege = (j) => j?.aggregate ?? j?.agregat ?? j?.agrege ?? j?.summary ?? j?.global ?? null;
const agregerCanaux = (campagnes) => {
  const somme = new Map();
  for (const c of campagnes) {
    for (const s of lireStatsCanaux(c)) {
      const k = String(s.canal || '').toLowerCase();
      const cur = somme.get(k) || { canal: s.canal, profils: 0, retenus: 0, scorePondere: 0, poids: 0 };
      const profils = Number(s.profils ?? s.profiles ?? 0);
      const score = s.scoreMoyen ?? s.score_moyen;
      cur.profils += profils;
      cur.retenus += Number(s.retenus ?? s.selected ?? 0);
      if (score != null && profils > 0) { cur.scorePondere += Number(score) * profils; cur.poids += profils; }
      somme.set(k, cur);
    }
  }
  return [...somme.values()].map(x => ({ canal: x.canal, profils: x.profils, retenus: x.retenus, scoreMoyen: x.poids > 0 ? x.scorePondere / x.poids : null }));
};
// Le canal en tête : le plus de retenus, départagé au score moyen. Nul si
// personne ne mène vraiment (égalité complète).
const canalEnTete = (stats) => {
  if (stats.length < 2) return null;
  const tri = [...stats].sort((a, b) => (b.retenus - a.retenus) || ((b.scoreMoyen ?? 0) - (a.scoreMoyen ?? 0)));
  if (tri[0].retenus === tri[1].retenus && (tri[0].scoreMoyen ?? 0) === (tri[1].scoreMoyen ?? 0)) return null;
  return tri[0].canal;
};
const lireStatsCanaux = (c) => {
  const s = c?.sourceStats ?? c?.source_stats ?? null;
  if (!s) return [];
  if (Array.isArray(s)) return s.map(x => ({ canal: x.canal ?? x.source ?? x.channel, ...x }));
  return Object.entries(s).map(([canal, x]) => ({ canal, ...(x || {}) }));
};
const enCourse = (c) => c?.running === true || ['running', 'pending', 'en_cours'].includes(String(c?.status ?? c?.statut ?? '').toLowerCase());

const DECISION_ICONE = { elargir: { icon: 'arrows-angle-expand', fg: 'var(--warn)' }, accepter: { icon: 'check-circle-fill', fg: 'var(--ok)' }, stop: { icon: 'x-circle-fill', fg: 'var(--danger-fg)' } };

/* Le DTO du dashboard passe TEL QUEL par le proxy (voulu : un champ ajouté
   chez Sourceur arrive sans redéploiement). Ses passes parlent donc SA
   langue — `pass`, `nb_retenus_cumules`, une `zone` qui peut n'être qu'un
   texte — alors que la frise et la vignette lisent le contrat front. Sans
   cette traduction, la frise affichait « passe undefined » et la vignette
   restait sans anneau. On ne fabrique rien : ce qui manque reste absent. */
const zoneFront = (z) => {
  if (!z) return null;
  if (typeof z === 'string') return { label: z };
  const out = {};
  if (z.niveau ?? z.level) out.niveau = String(z.niveau ?? z.level);
  if (z.label ?? z.ville ?? z.name) out.label = String(z.label ?? z.ville ?? z.name);
  if ((z.rayon_km ?? z.radiusKm) != null) out.rayon_km = z.rayon_km ?? z.radiusKm;
  if (z.centre ?? z.center) out.centre = z.centre ?? z.center;
  return Object.keys(out).length ? out : null;
};
const v2Front = (v2) => {
  if (!v2 || !Array.isArray(v2.passes)) return v2 ?? null;
  return { ...v2, passes: v2.passes.map((p, i) => ({
    ...p,
    n: p.n ?? p.pass ?? i + 1,
    zone: zoneFront(p.zone),
    nb_au_dessus_seuil: p.nb_au_dessus_seuil ?? p.nb_retenus_cumules ?? null,
    decision: p.decision === 'accepter' || p.decision === 'elargir' ? p.decision : (p.decision ? 'stop' : null),
  })) };
};
/* Les profils du dashboard portent `geo` (posé par le serveur) et
   `foundInPass` : la vignette lit `localisation` et `passe_origine`. */
const candidatsFront = (liste) => (Array.isArray(liste)
  ? liste.map(c => ({ ...c, localisation: c.localisation ?? c.geo ?? null, passe_origine: c.passe_origine ?? c.foundInPass ?? null }))
  : []);

/* ── La barre de course d'un canal : proportion retenus/profils, score moyen
      en mono. L'échelle est locale à la carte (max des profils des canaux)
      pour que les canaux d'une même campagne soient comparables entre eux. */
function BarreCanal({ stat, max }) {
  const profils = Number(stat.profils ?? stat.profiles ?? 0);
  const retenus = Number(stat.retenus ?? stat.selected ?? 0);
  const score = stat.scoreMoyen ?? stat.score_moyen ?? null;
  const largeur = max > 0 ? Math.max(2, (profils / max) * 100) : 0;
  const part = profils > 0 ? (retenus / profils) * 100 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <span style={{ width: '96px', flex: 'none', fontSize: '11.5px', fontWeight: 600, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{libelleCanal(stat.canal)}</span>
      <div style={{ flex: 1, height: '14px', borderRadius: '4px', background: 'var(--code-bg)', position: 'relative', overflow: 'hidden' }}
        title={`${profils} profils vus · ${retenus} retenus${score != null ? ` · score moyen ${Math.round(Number(score))}` : ''}`}>
        <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${largeur}%`, background: 'var(--accent-soft-bg, rgba(120,120,128,0.16))', transition: 'width .3s ease' }} />
        <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${(largeur * part) / 100}%`, background: 'var(--accent)', transition: 'width .3s ease' }} />
      </div>
      <span style={{ ...mono, fontSize: '11px', color: 'var(--text-2)', flex: 'none', width: '86px', textAlign: 'right' }}>
        {retenus}/{profils}{score != null ? ` · ${Math.round(Number(score))}` : ''}
      </span>
    </div>
  );
}

/* Frise v2 compacte : une puce par passe, la décision en icône. */
function FriseMini({ v2 }) {
  const passes = Array.isArray(v2?.passes) ? v2.passes : [];
  if (passes.length === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
      {passes.map((p, i) => {
        const dec = DECISION_ICONE[p.decision];
        return (
          <React.Fragment key={p.n ?? i}>
            {i > 0 && <BI icon="arrow-right-short" style={{ color: 'var(--text-faint)', fontSize: '12px' }} />}
            <span title={p.raison || undefined}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '6px', background: 'var(--chip-bg)', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
              <span style={{ ...mono, fontSize: '10px' }}>{p.n}</span>
              {p.zone?.label || p.zone?.niveau || `passe ${p.n}`}
              {p.nb_au_dessus_seuil != null && <span style={{ ...mono, fontSize: '10px', color: 'var(--text-3)' }}>{p.nb_au_dessus_seuil}≥s</span>}
              {dec && <BI icon={dec.icon} style={{ fontSize: '10px', color: dec.fg }} />}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function CarteCampagne({ c, onOpenBesoin, onSelectBesoin }) {
  const besoinId = c.besoin_id ?? c.besoinId ?? c.besoin?.id ?? null;
  // Mapping confirmé par Sourceur : titre = poste (+ ville en sous-texte).
  const titre = c.poste ?? c.besoin_titre ?? c.besoinTitre ?? c.titre ?? c.besoin?.titre ?? 'Campagne';
  const ville = c.ville ?? null;
  const stats = lireStatsCanaux(c);
  const maxProfils = Math.max(0, ...stats.map(s => Number(s.profils ?? s.profiles ?? 0)));
  const inmail = c.inmail ?? c.inmails ?? null;
  const tops = Array.isArray(c.topCandidates ?? c.top_candidates) ? (c.topCandidates ?? c.top_candidates) : [];
  const course = enCourse(c);
  // v2: null sur une campagne v1 → aucun élément v2 (frise, vignette).
  const v2 = v2Front(c.v2 ?? null);
  const estV2 = c.engine === 'v2' || v2 != null;
  const retenus = c.selectedCount ?? c.selected_count ?? null;
  const seuil = c.minScore ?? c.min_score ?? null;
  const passeActive = v2 ? (v2.currentPass ?? v2.current_pass ?? (Array.isArray(v2.passes) ? v2.passes.length : null)) : null;
  const passesMax = v2 ? (v2.maxPasses ?? v2.max_passes ?? v2.budget?.recherches_max ?? null) : null;
  return (
    <div style={{ ...CARD, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '11px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
        {/* Le titre mène au BESOIN ; « piloter » mène à la campagne. */}
        <button type="button" onClick={besoinId != null ? () => onOpenBesoin?.(besoinId) : undefined}
          title={besoinId != null ? 'Ouvrir le besoin' : undefined}
          style={{ background: 'none', border: 'none', padding: 0, cursor: besoinId != null ? 'pointer' : 'default', fontFamily: 'inherit', fontSize: '14px', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-strong)', textAlign: 'left', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
          {titre}
        </button>
        {ville && <span style={{ fontSize: '11.5px', color: 'var(--text-3)', flex: 'none' }}>· {ville}</span>}
        <span style={{ ...mono, fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '5px', border: '1px solid var(--border)', color: 'var(--text-2)', flex: 'none' }}>{estV2 ? 'v2' : 'v1'}</span>
        {passeActive != null && (
          <span title="Passe active du moteur v2" style={{ ...mono, fontSize: '10.5px', fontWeight: 600, color: 'var(--text-2)', flex: 'none' }}>
            passe {passeActive}{passesMax ? `/${passesMax}` : ''}
          </span>
        )}
        {course && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, color: 'var(--text-2)', flex: 'none' }}>
            <BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ats-spin .9s linear infinite', fontSize: '11px' }} /> en course
          </span>
        )}
        <span style={{ flex: 1 }} />
        {retenus != null && (
          <span title={`Profils retenus${seuil != null ? ` (seuil ${seuil})` : ''}`}
            style={{ ...mono, fontSize: '11.5px', fontWeight: 700, color: 'var(--text-strong)', flex: 'none' }}>
            {retenus} retenu{Number(retenus) > 1 ? 's' : ''}{seuil != null ? ` · ≥${seuil}` : ''}
          </span>
        )}
        {inmail && (inmail.total ?? 0) > 0 && (
          <span title="InMails envoyés / prévus" style={{ ...mono, fontSize: '11.5px', fontWeight: 600, color: 'var(--text-2)', background: 'var(--code-bg)', padding: '2px 8px', borderRadius: '6px', flex: 'none' }}>
            <BI icon="send" style={{ fontSize: '10px' }} /> {inmail.sent ?? 0}/{inmail.total}
          </span>
        )}
        {besoinId != null && (
          <button type="button" onClick={() => onSelectBesoin?.(besoinId)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '7px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer', flex: 'none' }}>
            Piloter <BI icon="chevron-right" style={{ fontSize: '10px' }} />
          </button>
        )}
      </div>

      {/* La course par canal + la vignette de la cible (handoff maquette) :
          cible et points seulement, sans étiquettes — le grand format vit
          dans le suivi de la campagne. */}
      {(stats.length > 0 || v2) && (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'stretch' }}>
          {stats.length > 0 && (
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px', justifyContent: 'center' }}>
              {stats.map((s, i) => <BarreCanal key={s.canal ?? i} stat={s} max={maxProfils} />)}
            </div>
          )}
          {v2 && <CibleMini v2={v2} candidates={candidatsFront(c.candidates ?? c.topCandidates ?? c.top_candidates)} />}
        </div>
      )}

      {v2 && <FriseMini v2={v2} />}

      {tops.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', flex: 'none' }}>En tête</span>
          {tops.slice(0, 3).map((p, i) => (
            <span key={p.recruiterId ?? i} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', fontWeight: 600, padding: '3px 9px', borderRadius: '7px', border: '1px solid var(--border)', background: 'var(--ghost-bg)', color: 'var(--text)', whiteSpace: 'nowrap' }}>
              {nomDe(p)}
              {p.score != null && <span style={{ ...mono, fontSize: '10.5px', color: 'var(--text-2)' }}>{Math.round(Number(p.score))}</span>}
              {p.cv === true && <span title="CV reçu" style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '9.5px', fontWeight: 700, color: 'var(--ok)' }}><BI icon="file-earmark-check" style={{ fontSize: '10px' }} />CV</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SourceurDashboard({ onOpenBesoin, onSelectBesoin }) {
  const [donnees, setDonnees] = useState(undefined); // undefined = jamais chargé
  const [absente, setAbsente] = useState(false);     // 404/501 → on arrête de sonner
  const [erreur, setErreur] = useState(null);        // texte serveur, verbatim
  const [maj, setMaj] = useState(null);
  const busy = useRef(false);

  const charger = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      const r = await fetch(`${BASE}/sourcing/dashboard`, { headers: authH() });
      if (r.status === 404 || r.status === 405 || r.status === 501) { setAbsente(true); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { setErreur(j.error || j.message || `HTTP ${r.status}`); return; }
      setErreur(null);
      setDonnees(j);
      setMaj(new Date());
    } catch (e) {
      // Réseau coupé : on garde le dernier état affiché, le prochain tick réessaie.
      if (donnees === undefined) setErreur(e.message || 'Réseau indisponible');
    } finally { busy.current = false; }
  }, [donnees]);

  useEffect(() => { charger(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (absente) return undefined;
    const iv = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      charger();
    }, 3000);
    return () => clearInterval(iv);
  }, [absente, charger]);

  if (absente) {
    return (
      <div style={{ ...CARD, marginTop: '20px', padding: '22px', fontSize: '13px', color: 'var(--text-2)' }}>
        Le suivi en direct arrive côté serveur — en attendant, la liste des campagnes reste disponible.
      </div>
    );
  }
  if (donnees === undefined && erreur) {
    return (
      <div style={{ ...CARD, marginTop: '20px', padding: '18px', border: '1px solid var(--danger-bd)', background: 'var(--danger-bg)' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--danger-fg)' }}>Le suivi n'a pas pu se charger</div>
        <div style={{ marginTop: '4px', fontSize: '12.5px', color: 'var(--danger-fg)', opacity: 0.9 }}>{erreur}</div>
        <button type="button" onClick={charger} style={{ marginTop: '10px', padding: '7px 12px', borderRadius: '8px', border: '1px solid var(--danger-bd)', background: 'transparent', color: 'var(--danger-fg)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>Réessayer</button>
      </div>
    );
  }
  if (donnees === undefined) {
    return (
      <div style={{ ...CARD, marginTop: '20px', padding: '26px', textAlign: 'center', fontSize: '13px', color: 'var(--text-faint)' }}>
        <BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ats-spin .9s linear infinite' }} /> Chargement du suivi…
      </div>
    );
  }

  const campagnes = lireCampagnes(donnees);
  const agrege = lireAgrege(donnees);
  // Panneau « qui gagne » : le champ serveur s'il existe, sinon la somme des
  // chiffres par campagne (convenu avec le back).
  const agStats = agrege ? lireStatsCanaux(agrege) : agregerCanaux(campagnes);
  const agMax = Math.max(0, ...agStats.map(s => Number(s.profils ?? s.profiles ?? 0)));
  const enTete = (agrege && (agrege.meilleur_canal || agrege.best_channel || agrege.gagnant)) || canalEnTete(agStats);

  return (
    <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', fontWeight: 600, padding: '3px 10px', borderRadius: '99px', background: 'var(--chip-bg)', color: 'var(--text-2)' }}>
          <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--ok)', animation: 'src-pulse 1.6s ease-in-out infinite' }} />
          en direct · toutes les 3 s
        </span>
        {maj && <span style={{ ...mono, fontSize: '11px', color: 'var(--text-faint)' }}>mis à jour à {maj.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>}
        {erreur && <span style={{ fontSize: '11.5px', color: 'var(--danger-fg)' }}>dernier rafraîchissement en échec : {erreur}</span>}
        <style>{'@keyframes src-pulse{0%,100%{opacity:1}50%{opacity:0.35}}'}</style>
      </div>

      {/* « Qui gagne » : qui ramène le plus, toutes campagnes confondues. */}
      {agStats.length > 0 && (
        <div style={{ ...CARD, padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '9px' }}>
            <span style={{ fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)' }}>Tous canaux, toutes campagnes</span>
            {enTete && (
              <span title="Le canal qui a retenu le plus de profils (à égalité, le meilleur score moyen)."
                style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--text-strong)' }}>
                <BI icon="arrow-up-short" style={{ fontSize: '11px', color: 'var(--warn)' }} /> {libelleCanal(enTete)}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {agStats.map((s, i) => <BarreCanal key={s.canal ?? i} stat={s} max={agMax} />)}
          </div>
        </div>
      )}

      {campagnes.length === 0 ? (
        <div style={{ ...CARD, padding: '26px', textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text)' }}>Aucune campagne à suivre</div>
          <div style={{ marginTop: '5px', fontSize: '12.5px', color: 'var(--text-2)' }}>Lancez une campagne depuis « Nouvelle campagne » — elle apparaîtra ici en direct.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(430px,1fr))', gap: '12px' }}>
          {campagnes.map((c, i) => (
            <CarteCampagne key={c.task_id ?? c.id ?? c.besoin_id ?? i} c={c} onOpenBesoin={onOpenBesoin} onSelectBesoin={onSelectBesoin} />
          ))}
        </div>
      )}
    </div>
  );
}
